/**
 * useFlowDraft
 * For Media Devoted | Created By Ciro Autiero
 *
 * Local-only draft edits for paths/landers/offers, with explicit Save/Reset.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { campaignFlowsKey } from '@/hooks/campaign-tracker';
import type { CampaignProvider } from '@/hooks/voluum/useVoluum';

import {
  buildDraftPaths,
  countDirty,
  markDraftSaved,
  togglePathDraft,
  toggleWeightedItemDraft,
  type DraftPath,
} from '../utils/flowDraft.utils';
import { useFlowDraftSave } from './draft-save/useFlowDraftSave';
import type { PathGroupRaw } from './useFlowDraft.binom-save';

function getDefaultSelectedPathId(paths: Array<{ id: string; active: boolean }>): string {
  const activePath = paths.find((p) => p.active);
  return String(activePath?.id ?? paths[0]?.id ?? '');
}

export function useFlowDraft(params: {
  flowId: string;
  campaignId?: string;
  /** Defaults to 'voluum' for back-compat. */
  provider?: CampaignProvider;
  /** Required for Binom save (server PUTs full Flow body); ignored for Voluum. */
  flowName?: string;
  pathGroupsRaw?: PathGroupRaw[];
  pathsRaw: Array<{
    id?: string;
    name?: string;
    groupId?: string;
    weight: number;
    active?: boolean;
    landers?: Array<{ id: string; name: string; weight: number }>;
    offers?: Array<{ id: string; name: string; weight: number }>;
  }>;
}) {
  const {
    flowId,
    campaignId,
    provider = 'voluum',
    flowName = '',
    pathGroupsRaw,
    pathsRaw,
  } = params;

  const [draftPaths, setDraftPaths] = useState<DraftPath[]>([]);
  const [selectedPathId, setSelectedPathId] = useState<string>('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const queryClient = useQueryClient();
  const saveForTracker = useFlowDraftSave({ flowId, campaignId, provider, flowName, pathGroupsRaw });

  useEffect(() => {
    setDraftPaths(buildDraftPaths({ paths: pathsRaw }));
  }, [pathsRaw]);

  useEffect(() => {
    if (draftPaths.length <= 0) return;
    const stillExists = draftPaths.some((p) => p.id === selectedPathId);
    if (!selectedPathId || !stillExists) {
      setSelectedPathId(getDefaultSelectedPathId(draftPaths));
    }
  }, [draftPaths, selectedPathId]);

  const selectedPath = useMemo(() => {
    if (draftPaths.length <= 0) return null;
    return draftPaths.find((p) => p.id === selectedPathId) ?? draftPaths[0];
  }, [draftPaths, selectedPathId]);

  const dirtyCount = useMemo(() => countDirty({ paths: draftPaths }), [draftPaths]);

  const resetDraft = useCallback(() => {
    setDraftPaths(buildDraftPaths({ paths: pathsRaw }));
  }, [pathsRaw]);

  const togglePathActive = useCallback((pathId: string, nextActive: boolean) => {
    setDraftPaths((current) =>
      current.map((p) => (p.id === pathId ? togglePathDraft({ path: p, nextActive }) : p)),
    );
  }, []);

  const toggleItemActive = useCallback(
    (params: { pathId: string; type: 'landers' | 'offers'; itemId: string; nextActive: boolean }) => {
      setDraftPaths((current) =>
        current.map((p) => {
          if (p.id !== params.pathId) return p;
          if (params.type === 'landers') {
            return {
              ...p,
              landers: p.landers.map((l) =>
                l.id === params.itemId ? toggleWeightedItemDraft({ item: l, nextActive: params.nextActive }) : l,
              ),
            };
          }
          return {
            ...p,
            offers: p.offers.map((o) =>
              o.id === params.itemId ? toggleWeightedItemDraft({ item: o, nextActive: params.nextActive }) : o,
            ),
          };
        }),
      );
    },
    [],
  );

  const saveDraft = useCallback(async () => {
    if (dirtyCount <= 0) return;
    setIsSavingDraft(true);
    try {
      await saveForTracker(draftPaths);
      setDraftPaths(markDraftSaved);

      if (campaignId) {
        await queryClient.invalidateQueries({ queryKey: campaignFlowsKey(provider, campaignId) });
      }
    } finally {
      setIsSavingDraft(false);
    }
  }, [campaignId, dirtyCount, draftPaths, provider, queryClient, saveForTracker]);

  return {
    draftPaths,
    selectedPath,
    selectedPathId,
    setSelectedPathId,
    dirtyCount,
    isSavingDraft,
    resetDraft,
    saveDraft,
    togglePathActive,
    toggleItemActive,
  };
}


